/**
 * Axios client wired for JWT auth.
 *
 * The admin SPA logs in with email + password and receives an access/refresh
 * JWT pair (see api/auth.ts), stored in localStorage. Every request carries
 * `Authorization: Bearer <access>`. On a 401 the client transparently refreshes
 * once via /api/v1/auth/token/refresh/ and retries the original request; if the
 * refresh fails the session is cleared and the user is sent to /login.
 *
 * In dev, Vite proxies /api/* to the Django backend on :8000 (vite.config.ts),
 * so VITE_API_BASE is empty and calls are same-origin. In prod, VITE_API_BASE
 * points at https://api.hktada.org (cross-origin; JWT needs no cookies/CSRF).
 *
 * All responses use the envelope {success, data} | {success, error}; the api/*
 * modules unwrap `.data.data` and surface `.data.error` on failure.
 */
import axios from "axios";

const baseURL = (import.meta.env.VITE_API_BASE || "").replace(/\/+$/, "");

const ACCESS_KEY = "hktada_admin_access";
const REFRESH_KEY = "hktada_admin_refresh";

export const tokenStore = {
  get access() {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(access, refresh) {
    localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

export const http = axios.create({
  baseURL,
  headers: { Accept: "application/json" },
});

http.interceptors.request.use((config) => {
  const token = tokenStore.access;
  if (token) config.headers.set("Authorization", `Bearer ${token}`);
  return config;
});

// Single-flight refresh: concurrent 401s share one refresh round-trip.
let refreshing = null;

async function refreshAccessToken() {
  const refresh = tokenStore.refresh;
  if (!refresh) return null;
  try {
    // Bare axios (not `http`) to skip the interceptors and avoid recursion.
    const { data } = await axios.post(
      `${baseURL}/api/v1/auth/token/refresh/`,
      { refresh },
      { headers: { Accept: "application/json" } },
    );
    const access = data?.data?.access;
    if (!access) return null;
    tokenStore.set(access, data?.data?.refresh);
    return access;
  } catch {
    return null;
  }
}

function redirectToLogin() {
  tokenStore.clear();
  if (location.pathname !== "/login") {
    location.href = `/login?next=${encodeURIComponent(location.pathname + location.search)}`;
  }
}

// Listeners notified after every successful MUTATING admin call (POST/PATCH/
// PUT/DELETE under /api/v1/admin/, except the rebuild trigger itself). The
// pending-changes store subscribes, so the「尚未發佈」bar refreshes right after
// any save without each api/* module having to remember to ping it.
const mutationListeners = new Set();

export function onAdminMutation(listener) {
  mutationListeners.add(listener);
  return () => mutationListeners.delete(listener);
}

function notifyAdminMutation(config) {
  const method = (config?.method || "get").toLowerCase();
  const url = config?.url || "";
  if (method === "get" || !url.includes("/api/v1/admin/") || url.includes("/rebuild")) return;
  for (const listener of mutationListeners) {
    try { listener(); } catch { /* a listener error must not break the API call */ }
  }
}

http.interceptors.response.use(
  (resp) => {
    notifyAdminMutation(resp.config);
    return resp;
  },
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url ?? "";
    const isAuthCall = url.includes("/auth/login") || url.includes("/auth/token/refresh");

    if (status === 401 && original && !original._retried && !isAuthCall && tokenStore.refresh) {
      original._retried = true;
      refreshing = refreshing ?? refreshAccessToken();
      const newAccess = await refreshing;
      refreshing = null;
      if (newAccess) {
        original.headers.set("Authorization", `Bearer ${newAccess}`);
        return http(original);
      }
      redirectToLogin();
    }
    return Promise.reject(error);
  },
);
